import React, { useState, useContext } from 'react';
import { CurrencyContext } from '../../context/CurrencyContext';
import '../MortgageCalculator.css';

const AffordabilityCalculator: React.FC = () => {
  const [income, setIncome] = useState<string>('80000');
  const [downPayment, setDownPayment] = useState<string>('60000');
  const [debt, setDebt] = useState<string>('500');
  const [interestRate, setInterestRate] = useState<string>('5.5');
  const [loanTerm, setLoanTerm] = useState<string>('30');
  const [affordablePrice, setAffordablePrice] = useState<number | null>(null);
  const [monthlyPayment, setMonthlyPayment] = useState<number | null>(null);
  
  const { currencySymbol } = useContext(CurrencyContext);

  // Format currency for better readability
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0
    }).format(amount);
  };

  const calculateAffordability = () => {
    // Monthly income
    const monthlyIncome = parseFloat(income) / 12;
    // Max debt-to-income ratio typically 36-43%, using 36%
    const maxDTI = 0.36;
    // Monthly debt payments
    const monthlyDebt = parseFloat(debt);
    // Max mortgage payment allowed (including property tax, insurance)
    const maxPITI = monthlyIncome * maxDTI - monthlyDebt;
    // Estimate tax and insurance at 25% of mortgage payment
    const maxPI = maxPITI * 0.75;
    
    // Calculate affordable mortgage amount
    const monthlyRate = parseFloat(interestRate) / 100 / 12;
    const termMonths = parseInt(loanTerm) * 12;
    
    if (monthlyRate > 0 && termMonths > 0) {
      // Present value of an annuity formula
      const affordableMortgage = maxPI * ((1 - Math.pow(1 + monthlyRate, -termMonths)) / monthlyRate);
      // Add down payment to get total affordable price
      const totalAffordable = affordableMortgage + parseFloat(downPayment);
      
      setAffordablePrice(totalAffordable);
      setMonthlyPayment(maxPI);
    } else {
      setAffordablePrice(null);
      setMonthlyPayment(null);
    }
  };

  return (
    <div className="calculator">
      <h2>Home Affordability Calculator</h2>
      
      <div className="input-group">
        <label htmlFor="income">Annual Income ({currencySymbol})</label>
        <input
          id="income"
          type="number"
          value={income}
          onChange={(e) => setIncome(e.target.value)}
        />
      </div>
      
      <div className="input-group">
        <label htmlFor="downPayment">Down Payment ({currencySymbol})</label>
        <input
          id="downPayment"
          type="number"
          value={downPayment}
          onChange={(e) => setDownPayment(e.target.value)}
        />
      </div>
      
      <div className="input-group">
        <label htmlFor="debt">Monthly Debt Payments ({currencySymbol})</label>
        <input
          id="debt"
          type="number"
          value={debt}
          onChange={(e) => setDebt(e.target.value)}
        />
      </div>
      
      <div className="input-group">
        <label htmlFor="interestRate">Interest Rate (%)</label>
        <input
          id="interestRate"
          type="number"
          step="0.01"
          value={interestRate}
          onChange={(e) => setInterestRate(e.target.value)}
        />
      </div>
      
      <div className="input-group">
        <label htmlFor="loanTerm">Loan Term (years)</label>
        <input
          id="loanTerm"
          type="number"
          value={loanTerm}
          onChange={(e) => setLoanTerm(e.target.value)}
        />
      </div>
      
      <button onClick={calculateAffordability}>Calculate Affordability</button>
      
      {affordablePrice !== null && (
        <div className="result">
          <h3>Affordable Home Price</h3>
          <p>{currencySymbol}{formatCurrency(affordablePrice)}</p>
          
          <div className="result-breakdown">
            <div className="result-item">
              <span className="result-item-label">Down Payment:</span>
              <span className="result-item-value">{currencySymbol}{formatCurrency(parseFloat(downPayment))}</span>
            </div>
            <div className="result-item">
              <span className="result-item-label">Monthly Payment:</span>
              <span className="result-item-value">
                {monthlyPayment !== null ? `${currencySymbol}${monthlyPayment.toFixed(2)}` : '-'}
              </span>
            </div>
            <div className="result-item">
              <span className="result-item-label">Mortgage Amount:</span>
              <span className="result-item-value">
                {currencySymbol}{formatCurrency(affordablePrice - parseFloat(downPayment))}
              </span>
            </div>
            <div className="result-item">
              <span className="result-item-label">Annual Income:</span>
              <span className="result-item-value">{currencySymbol}{formatCurrency(parseFloat(income))}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AffordabilityCalculator;